import { ChevronRightIcon, TicketPercentIcon } from 'lucide-react';

import { cn } from '@/shared/lib/utils';

import type { CouponProgress } from '../utils/store-benefits';
import { couponShortLabel, formatShortBRL } from '../utils/store-benefits';

interface StoreBenefitsBarProps {
  progress: CouponProgress;
  /** Quantos cupons a loja tem (mostra "1/3"). */
  total: number;
  isSelected: boolean;
  onClick: () => void;
  className?: string;
}

/**
 * Linha de cupom no rodapé da loja (PRAT-110; enxuta no PRAT-154): o melhor
 * cupom e quanto falta para liberar o pedido mínimo, com um fio de progresso.
 * Toque abre a lista de cupons.
 */
export function StoreBenefitsBar({
  progress,
  total,
  isSelected,
  onClick,
  className,
}: StoreBenefitsBarProps) {
  const { coupon, unlocked, missing, percent } = progress;
  const label = couponShortLabel(coupon);

  const title = unlocked
    ? isSelected
      ? `Cupom de ${label} escolhido`
      : `Cupom de ${label} liberado`
    : `Faltam ${formatShortBRL(missing)} para ${label}`;

  const subtitle = unlocked
    ? isSelected
      ? 'Ele entra sozinho no seu pedido'
      : 'Toque para usar no seu pedido'
    : `Pedido mínimo ${formatShortBRL(Number(coupon.min_subtotal))}`;

  // PRAT-154: uma linha só, discreta, acima do Ver sacola (antes era um card
  // com selo, barra e duas linhas de texto, e o rodapé ficava poluído).
  const action = unlocked
    ? isSelected
      ? 'Aplicado'
      : 'Usar'
    : total > 1
      ? `${total} cupons`
      : 'Ver';

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`${title}. ${subtitle}`}
      className={cn(
        'relative flex w-full items-center gap-2 overflow-hidden py-2 text-left clickable-button',
        className
      )}
    >
      <TicketPercentIcon
        className={cn('size-4 shrink-0', unlocked ? 'text-emerald-600' : 'text-gray-500')}
        aria-hidden
      />
      <span className="min-w-0 flex-1 truncate text-[13px] text-gray-700">
        <span className={cn('font-semibold', unlocked ? 'text-emerald-700' : 'text-night')}>
          {title}
        </span>
      </span>
      <span className="flex shrink-0 items-center text-xs font-semibold text-primary">
        {action}
        <ChevronRightIcon className="size-3.5" aria-hidden />
      </span>
      {!unlocked && (
        <span
          className="absolute inset-x-0 bottom-0 h-0.5 bg-gray-100"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(percent)}
          aria-label={subtitle}
        >
          <span
            className="block h-full bg-emerald-500 transition-[width] duration-500"
            style={{ width: `${Math.round(percent)}%` }}
          />
        </span>
      )}
    </button>
  );
}
